import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import type { SummaryEntityCategory } from '../../../../../redux/slices/InsightsSlice';
import { computeGrossMargin, type GrossMarginResult } from '../computeGrossMargin';
import {
  resolveCropGroupLabel,
  resolveInputAreaHa,
  resolveInputCrop,
  STEP_TYPE_TO_SUMMARY_ENTITY,
} from './summary2Utils';

export interface PlanVsActualRow {
  key: string;
  cropLabel: string;
  planCost: number;
  actualCost: number;
  /** Summed area (ha) of the row's constituent inputs — divides planCost/actualCost for the $/ha display mode. */
  areaHa: number;
}

/** 'all' blends every cost step type (today's default view); any other value scopes to just that input category. */
export type PlanVsActualCategoryFilter = 'all' | SummaryEntityCategory;

export const PLAN_VS_ACTUAL_CATEGORY_FILTER_ALL: PlanVsActualCategoryFilter =
  'all';

/** 'total' = $ totals (today's default); 'perHa' divides by area — shared by the Plan vs Actual table/bar-chart and the Spend Over Time timeline. */
export type SpendMetric = 'total' | 'perHa';

const PLAN_ONLY = new Set([InputTaskCategory.Plan]);
const ACTUAL_ONLY = new Set([InputTaskCategory.Actual]);

/** Blended total (today's default) or one category's slice of the cost breakdown. */
const resolveCost = (
  gm: GrossMarginResult,
  categoryFilter: PlanVsActualCategoryFilter
): number => {
  if (categoryFilter === 'all') return Math.abs(gm.tvcTotal);
  return gm.costBreakdown.reduce(
    (sum, line) =>
      STEP_TYPE_TO_SUMMARY_ENTITY[line.stepType] === categoryFilter
        ? sum + line.totalCost
        : sum,
    0
  );
};

/**
 * One row per resolved crop/variety — total Plan-task cost vs total
 * Actual-task cost, for the Plan vs Actual Spend section. Reuses the
 * existing, unmodified `computeGrossMargin` (called once per task category
 * per input, same as the Analysis lens does) rather than re-deriving cost
 * math. `categoryFilter` scopes both sides to one input category (Seed,
 * Soil Input, Plant Protection, Seed Dress, Biostimulant, Operation) by
 * reading `costBreakdown` instead of the blended `tvcTotal`.
 */
export const computePlanVsActual = (
  inputs: IInput[],
  gUnits: GUnit[],
  categoryFilter: PlanVsActualCategoryFilter = PLAN_VS_ACTUAL_CATEGORY_FILTER_ALL
): PlanVsActualRow[] => {
  const rows = new Map<string, PlanVsActualRow>();

  for (const input of inputs) {
    const planCost = resolveCost(
      computeGrossMargin(input, PLAN_ONLY, gUnits),
      categoryFilter
    );
    const actualCost = resolveCost(
      computeGrossMargin(input, ACTUAL_ONLY, gUnits),
      categoryFilter
    );
    if (planCost === 0 && actualCost === 0) continue;

    const resolved = resolveInputCrop(input);
    const cropLabel = resolveCropGroupLabel(resolved.crop, resolved.variety);

    const existing = rows.get(cropLabel) ?? {
      key: cropLabel,
      cropLabel,
      planCost: 0,
      actualCost: 0,
      areaHa: 0,
    };
    existing.planCost += planCost;
    existing.actualCost += actualCost;
    existing.areaHa += resolveInputAreaHa(input, gUnits);
    rows.set(cropLabel, existing);
  }

  return Array.from(rows.values()).sort((a, b) =>
    a.cropLabel.localeCompare(b.cropLabel, undefined, { sensitivity: 'base' })
  );
};
